import type { QueryKey } from '@tanstack/react-query';

/**
 * Unsent composer text for saved conversations, kept in memory by one host (the Atlas expert chat) while it lives.
 * Never written to browser storage, a cookie or the server, and never logged.
 */

/** Who may see remembered text: the verified viewer, the expert's current owner and the expert. */
export interface ConversationComposerDraftAudience {
  viewerId: string;
  ownerId: string;
  expertId: string;
}

export interface ConversationComposerDraftStore {
  /** Bumped by every `clear`: text remembered under an older generation is gone and cannot be written again. */
  generation(): number;
  read(key: string): string | undefined;
  /** Remembers `text` (empty forgets it), only while `writtenIn` is still the current generation. */
  write(key: string, text: string, writtenIn: number): void;
  /** Forgets the text, only while `removedIn` is still the current generation. */
  remove(key: string, removedIn: number): void;
  clear(): void;
  /** Whether any text is remembered now; never says which or what. */
  hasDrafts(): boolean;
  /**
   * Calls `listener` synchronously each time `hasDrafts()` changes through an accepted write, removal or clear (never
   * for a refused stale-generation call). Returns the unsubscribe.
   */
  subscribePresence(listener: (hasDrafts: boolean) => void): () => void;
  /**
   * A private, independent copy of the remembered text, for the App's route draft boundary when a host leaves Atlas.
   * Observational: this store, its generation and its listeners are unchanged, and nothing is serialized or logged.
   */
  fork(): ConversationComposerDraftFork;
}

/** A copy taken by `fork`. */
export interface ConversationComposerDraftFork {
  /** The copied store's generation when the copy was taken. */
  sourceGeneration: number;
  /** A separate in-memory store with the same text and a generation of its own (never the source's). */
  store: ConversationComposerDraftStore;
}

/** What a host hands its chat screen. `audience` is null while any part of it is unknown. */
export interface ConversationComposerDrafts {
  audience: ConversationComposerDraftAudience | null;
  /** The store's generation when the host rendered. */
  generation: number;
  store: ConversationComposerDraftStore;
}

/** The saved conversation a screen loaded for an audience: its edits, send and deletion address this key only. */
export interface LoadedConversationComposerDraft {
  key: string;
  conversationId: string;
  generation: number;
  store: ConversationComposerDraftStore;
}

export function conversationComposerDraftKey(
  audience: ConversationComposerDraftAudience,
  conversationId: string
): string {
  return JSON.stringify([audience.viewerId, audience.ownerId, audience.expertId, conversationId]);
}

/** Changes whenever the audience or the store's session does, so a screen knows its shown text no longer belongs. */
export function conversationComposerDraftAudienceKey(drafts: ConversationComposerDrafts): string {
  const { audience } = drafts;
  return JSON.stringify([
    drafts.generation,
    audience ? [audience.viewerId, audience.ownerId, audience.expertId] : null,
  ]);
}

/**
 * The audience and session an action starts in, including the store's live generation (a session can end before the
 * host renders again). An action that completes in another context must not act on remembered text or the screen.
 * Empty without a bridge.
 */
export function conversationComposerDraftContext(
  drafts: ConversationComposerDrafts | undefined
): string {
  return drafts
    ? `${conversationComposerDraftAudienceKey(drafts)}|${drafts.store.generation()}`
    : '';
}

/** The authenticated viewer query (`useGetLoggedInUser`): removing it, or clearing the cache, ends the session. */
export function isViewerQueryKey(queryKey: QueryKey): boolean {
  return queryKey.length === 1 && queryKey[0] === 'user';
}

export function createConversationComposerDraftStore(): ConversationComposerDraftStore {
  return draftStoreWith(new Map(), 0);
}

function draftStoreWith(
  texts: Map<string, string>,
  initialGeneration: number
): ConversationComposerDraftStore {
  let generation = initialGeneration;
  const presenceListeners = new Set<(hasDrafts: boolean) => void>();
  // Reports a change of presence only (no keys or text).
  const reportPresence = (before: boolean) => {
    const now = texts.size > 0;
    if (now === before) return;
    for (const listener of Array.from(presenceListeners)) listener(now);
  };
  return {
    generation: () => generation,
    read: (key) => texts.get(key),
    write: (key, text, writtenIn) => {
      if (writtenIn !== generation) return;
      const before = texts.size > 0;
      if (text.length > 0) texts.set(key, text);
      else texts.delete(key);
      reportPresence(before);
    },
    remove: (key, removedIn) => {
      if (removedIn !== generation) return;
      const before = texts.size > 0;
      texts.delete(key);
      reportPresence(before);
    },
    clear: () => {
      const before = texts.size > 0;
      texts.clear();
      generation += 1;
      reportPresence(before);
    },
    hasDrafts: () => texts.size > 0,
    subscribePresence: (listener) => {
      presenceListeners.add(listener);
      return () => {
        presenceListeners.delete(listener);
      };
    },
    fork: () => ({
      sourceGeneration: generation,
      store: draftStoreWith(new Map(texts), generation + 1),
    }),
  };
}
